import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { Formik, Form } from 'formik';
import { updateUserInfo } from '../../API/APICalls';
import PasswordChange from './PasswordChange';
import Textfield from '../FormsUI/Textfield';
import Checkbox from '../FormsUI/Checkbox';
import SnackbarShow from '../SnackbarShow';
import Button from '../FormsUI/Button';
import Select from '../FormsUI/Select';
import { checkBoxStyle, boxStyle, formStyle } from '../../Utils/Constants';
import { userEditFormValidation } from '../../Utils/UserValidations';
import { validateQuotaForEdit } from '../../Utils/HelperFunctions';
import ReadOnlyDisplay from '../ReadOnlyDisplay';



const UserEditForm = ({ id, user, setWindowClose ,onUpdateSuccess }) => {
    const [snackbarMessage, setSnackbarMessage] = useState({ open: false, message: '', severity: '' });
    const [policyData, setPolicyData] = useState('');
    const [accessRestrictionData, setAccessRestrictionData] = useState('');
    const [connectorData, setConnectorData] = useState('');
    const [restrictCountryCode, setRestrictCountryCode] = useState('');
    const [allowNets, setAllowNets] = useState('');

    useEffect(() => {
        // check if sesison is valid for every 1 minutes
        const interval = setInterval(() => {
            const expiryTime = window.sessionStorage.getItem('expiry_time');
            if (expiryTime && Date.now() > expiryTime) {
                setSnackbarMessage({ open: true, message: 'Session expired. Please login again.', severity: 'error' });
                window.close();
            }
        }, 6 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleSubmitForm = async (values) => {
        
        try {
            const old_allocated_space = JSON.parse(sessionStorage.getItem('userData')).mailbox_data.allocated_space;
            await validateQuotaForEdit(id, values.domain_id, values.allocated_space, old_allocated_space, values.used_quota);
            if (window.confirm('Are you sure you want to update the user details?')) {
                let response = await updateUserInfo(id, values);

                if (onUpdateSuccess) {
                    await onUpdateSuccess();
                }
                setSnackbarMessage({
                    open: response.status === 200,
                    message: response.status === 200 ? 'User details updated successfully' : `User updation failed Status code: ${response.status} Message: ${response.data}`,
                    severity: response.status === 200 ? 'success' : 'error'
                })
            };
            setWindowClose(true)
        } catch (error) {
            setSnackbarMessage({ open: true, message: `User updation failed: ${error}`, severity: 'error' });
        };
    }

    const makeFormData = async (user) => {
        if (user?.available_policies) {
            setPolicyData(user.available_policies.reduce((acc, curr) => {
                acc[curr.policy_id] = curr.policy_name;
                return acc;
            }, {}));
        }
        if (user?.access_restrictions?.access_restriction_options) {
            setAccessRestrictionData(user.access_restrictions.access_restriction_options.reduce((acc, curr) => {
                acc[curr.id] = curr.access_master;
                return acc;
            }, {}));
        }
        if (user?.connectors) {
            setConnectorData(user.connectors.reduce((acc, curr) => {
                acc[curr.connector_id] = `${curr.connector_id} - ${curr.connector_description}`;
                return acc;
            }, {}));
        }
        if (user?.country_codes) {
            setRestrictCountryCode(user.country_codes.reduce((acc, curr) => {
                acc[curr.id] = `${curr.code} - ${curr.name}`
                return acc;
            }, {}));
        }
        if (user?.mailbox_data?.enable_access_restriction) {
            setAllowNets(user.access_restrictions.allow_nets);
        } else {
            setAllowNets('0.0.0.0/0');
        };
    };

    useEffect(() => {
        makeFormData(user);
    }, [user]);

    const snackbarMessageComp = snackbarMessage.open
        ? (<SnackbarShow data={snackbarMessage} setSnackbarMessage={setSnackbarMessage} />)
        : null;

    return (
        <>
            {snackbarMessageComp}
            <Formik initialValues={user.mailbox_data} validationSchema={userEditFormValidation} onSubmit={handleSubmitForm}>
                <Form style={formStyle}>
                    <Typography variant='h6' sx={{ mb: 1, fontSize: '1.25rem' }}>Edit '{user.mailbox_data.local_part}@{user.mailbox_data.domain_name}' mailbox details:</Typography>
                    
                    <Typography variant='subtitle2' sx={{ mt: 0.5, mb: 0.5 }} color="textSecondary">Basic Information</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='first_name' label='First Name' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='last_name' label='Last Name' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='mobile_prefix' label='Mobile Prefix (Country Code)' type='number' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='mobile' label='Mobile Number' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='employee_id' label='Employee ID' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='designation' label='Designation' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='phone' label='Phone' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='address' label='Address' />
                        </Grid>
                    </Grid>

                    <Typography variant='subtitle2' sx={{ mt: 1.5, mb: 0.5 }} color="textSecondary">Account Settings</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='allocated_space' label='Allocated space in GB' type='number' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Textfield name='email_other' label='Alternate email id (will get 2FA message)' />
                        </Grid>
                        
                        {policyData && (
                            <Grid item xs={12} sm={4}>
                                <Select name='policy_id' label='Mailbox Policy' options={policyData} noneRequired={true} />
                            </Grid>
                        )}
                        {accessRestrictionData && (
                            <Grid item xs={12} sm={4}>
                                <Select name='access_restriction_master_id' label='Access restriction master' options={accessRestrictionData} noneRequired={true} />
                            </Grid>
                        )}
                        {restrictCountryCode && (
                            <Grid item xs={12} sm={4}>
                                <Select name='country_info' label='Country based restriction' options={restrictCountryCode} noneRequired={false} multiple />
                            </Grid>
                        )}
                        
                        {connectorData && (
                            <Grid item xs={12} sm={4}>
                                <Select name='incoming_connector_id' label='Incoming routing' noneRequired={true} options={connectorData} />
                            </Grid>
                        )}
                        {connectorData && (
                            <Grid item xs={12} sm={4}>
                                <Select name='outgoing_connector_id' label='Outgoing routing' noneRequired={true} options={connectorData} />
                            </Grid>
                        )}
                        {user.available_departments && (
                            <Grid item xs={12} sm={4}>
                                <Select name='department_id' label='Department' noneRequired={true} options={user.available_departments} />
                            </Grid>
                        )}
                    </Grid>

                    <Typography variant='subtitle2' sx={{ mt: 1.5, mb: 0.5 }} color="textSecondary">Permissions & Flags</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='active' label='Active' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='two_fa_enabled' label='Enable 2FA' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='is_domain_admin' label='Is domain admin' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='is_hybrid' label='Is hybrid' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='spam_filter_bypass' label='Spam filter bypass' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='mailarchive' label='Mail Archive' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='enable_access_restriction' label='Enable access restriction' />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox name='disable_password_expiration' label='Disable password expiration' />
                        </Grid>
                    </Grid>

                    <ReadOnlyDisplay userData={user} allowNets={allowNets} />
                    <PasswordChange id={id} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5, mb: 1 }}>
                        <Button variant='contained' color='primary' sx={{ width: '35%' }} type='submit'>Update</Button>
                    </Box>
                </Form>
            </Formik>
        </>
    )
}

export default UserEditForm;